import { EventTypes, ImageObjectFitTypes } from '../render/constants';
import { getNodeClip, serializeClip } from '../render/utils/image-clip';
import { renderEdgeHandler, renderPointHandler } from './interaction/render';
import { Rect, Region } from './region';

export function getNodeRectInStage(stageContext, node) {
  const region = stageContext.api.getNodeRegion(node);
  return { x: region.rect.x, y: region.rect.y, w: region.rect.w, h: region.rect.h };
}

export function computeClipBoxInStage(stageContext, node) {
  // Use node's rotated region as basis for all math
  const nodeRegion = stageContext.api.getNodeRegion(node);
  const TL = nodeRegion.topLeft;
  const TR = nodeRegion.topRight;
  const BL = nodeRegion.bottomLeft;
  const u0 = { x: TR.x - TL.x, y: TR.y - TL.y }; // width vector in stage space
  const v0 = { x: BL.x - TL.x, y: BL.y - TL.y }; // height vector in stage space
  const nodeW = Math.hypot(u0.x, u0.y) || 1;
  const nodeH = Math.hypot(v0.x, v0.y) || 1;
  const ux = u0.x / nodeW;
  const uy = u0.y / nodeW;
  const vx = v0.x / nodeH;
  const vy = v0.y / nodeH;

  const aabb = nodeRegion.rect;
  const clip = getNodeClip(node) || { x: 0, y: 0, w: 0, h: 0 };
  const _img = node._img?.img;

  if (!_img) {
    return {
      x: aabb.x,
      y: aabb.y,
      w: aabb.w,
      h: aabb.h,
      rect: aabb,
      clip,
      region: nodeRegion,
    };
  }

  // Original image dimensions
  const DPR = stageContext.dpr;
  const originalW = _img.width;
  const originalH = _img.height;

  // Clipped image virtual size in original image space
  const clippedW = originalW * (1 + clip.w);
  const clippedH = originalH * (1 + clip.h);
  const clippedRatio = (clippedW || 1) / (clippedH || 1);

  // Emulate getClipImage scale limit effect for scale-down logic only
  const MAX_DIMENSION = 8192;

  const targetW = Math.max(1, Math.round(clippedW * DPR));
  const targetH = Math.max(1, Math.round(clippedH * DPR));
  const scaleX = targetW > MAX_DIMENSION ? MAX_DIMENSION / targetW : 1;
  const scaleY = targetH > MAX_DIMENSION ? MAX_DIMENSION / targetH : 1;
  const scale = Math.min(scaleX, scaleY);
  const zoom = DPR * scale;

  // This represents the actual pixel dimensions of the clipped image used in rendering
  const pixelW = clippedW * zoom;
  const pixelH = clippedH * zoom;

  // Compute where the clipped image is drawn inside the node (pre-radius clip),
  // mirroring render/image.js calculateImageDimensions behavior, but in node-local coordinates
  const fit = node.fit || (ImageObjectFitTypes ? ImageObjectFitTypes.fill : 'fill');
  const targetRatio = (nodeW || 1) / (nodeH || 1);

  let drawWidth = nodeW;
  let drawHeight = nodeH;
  let offsetX = 0;
  let offsetY = 0;

  if (fit === (ImageObjectFitTypes ? ImageObjectFitTypes.cover : 'cover')) {
    if (clippedRatio > targetRatio) {
      drawHeight = nodeH;
      drawWidth = nodeH * clippedRatio;
      offsetY = 0;
      offsetX = (nodeW - drawWidth) / 2;
    } else {
      drawWidth = nodeW;
      drawHeight = nodeW / clippedRatio;
      offsetX = 0;
      offsetY = (nodeH - drawHeight) / 2;
    }
  } else if (fit === (ImageObjectFitTypes ? ImageObjectFitTypes.contain : 'contain')) {
    if (clippedRatio > targetRatio) {
      drawWidth = nodeW;
      drawHeight = nodeW / clippedRatio;
      offsetX = 0;
      offsetY = (nodeH - drawHeight) / 2;
    } else {
      drawHeight = nodeH;
      drawWidth = nodeH * clippedRatio;
      offsetY = 0;
      offsetX = (nodeW - drawWidth) / 2;
    }
  } else if (
    fit === (ImageObjectFitTypes ? ImageObjectFitTypes.scaleDown : 'scale-down') ||
    fit === 'scaleDown'
  ) {
    // Start from contain size, then limit by intrinsic pixel size if smaller
    let containW, containH, containX, containY;
    if (clippedRatio > targetRatio) {
      containW = nodeW;
      containH = nodeW / clippedRatio;
      containX = 0;
      containY = (nodeH - containH) / 2;
    } else {
      containH = nodeH;
      containW = nodeH * clippedRatio;
      containY = 0;
      containX = (nodeW - containW) / 2;
    }
    if (containW > pixelW || containH > pixelH) {
      drawWidth = pixelW;
      drawHeight = pixelH;
      offsetX = (nodeW - drawWidth) / 2;
      offsetY = (nodeH - drawHeight) / 2;
    } else {
      drawWidth = containW;
      drawHeight = containH;
      offsetX = containX;
      offsetY = containY;
    }
  } else if (fit === 'top') {
    drawWidth = nodeW;
    drawHeight = nodeW / clippedRatio;
    offsetX = 0;
    offsetY = 0;
  } else {
    // fill, none, or default
    drawWidth = nodeW;
    drawHeight = nodeH;
    offsetX = 0;
    offsetY = 0;
  }

  // Base draw origin in stage space using node's rotated basis
  const baseOrigin = {
    x: TL.x + ux * offsetX + vx * offsetY,
    y: TL.y + uy * offsetX + vy * offsetY,
  };

  const baseW = drawWidth;
  const baseH = drawHeight;

  // Map original image region inside the clipped canvas to stage space.
  // Entire clipped canvas maps to base rect in node-local space (parallelogram in stage space).
  const sX = baseW / (clippedW || 1);
  const sY = baseH / (clippedH || 1);
  const contentLocalW = sX * originalW;
  const contentLocalH = sY * originalH;
  const offsetLocalX = sX * (originalW * (clip.x || 0));
  const offsetLocalY = sY * (originalH * (clip.y || 0));

  // Top-left of content in stage space
  const contentTL = {
    x: baseOrigin.x - ux * offsetLocalX - vx * offsetLocalY,
    y: baseOrigin.y - uy * offsetLocalX - vy * offsetLocalY,
  };

  const contentTR = {
    x: contentTL.x + ux * contentLocalW,
    y: contentTL.y + uy * contentLocalW,
  };
  const contentBL = {
    x: contentTL.x + vx * contentLocalH,
    y: contentTL.y + vy * contentLocalH,
  };
  const contentBR = {
    x: contentTR.x + vx * contentLocalH,
    y: contentTR.y + vy * contentLocalH,
  };

  const contentRegion = new Region({
    topLeft: contentTL,
    topRight: contentTR,
    bottomRight: contentBR,
    bottomLeft: contentBL,
  });
  const bb = contentRegion.rect;

  return {
    x: bb.x,
    y: bb.y,
    w: bb.w,
    h: bb.h,
    rect: bb,
    clip,
    region: contentRegion,
    // provide useful deriveds for rendering
    __derived: {
      nodeRegion,
      nodeW,
      nodeH,
      contentLocalW,
      contentLocalH,
      contentTL,
      contentTR,
      contentBL,
      contentBR,
    },
  };
}

export function renderClipOverlay(stageContext, node) {
  const { scale, interactive } = stageContext.providers;
  const region = stageContext.api
    .getNodeRegion(node)
    .transform(scale.scale, scale.translateX, scale.translateY);
  // Render viewport handlers (resize node bounds)
  const viewportHandlers = getViewportHandlers(stageContext, region);
  const { context } = stageContext;

  for (const handler of viewportHandlers) {
    const handlerId = handler.id;
    const shape = handler.rect;

    if (shape && typeof shape.x === 'number') {
      // Corner handlers - use renderPointHandler
      const area = {
        id: handlerId,
        center: { x: shape.x + shape.w / 2, y: shape.y + shape.h / 2 },
        w: shape.w,
        h: shape.h,
        rect: shape,
      };
      const isActive = area.id === interactive.hoverHandlerId;
      renderPointHandler(context, area, isActive);
    } else if (shape && shape.topLeft) {
      // Edge handlers - pass Region directly to renderEdgeHandler for proper angle support
      const isActive = handlerId === interactive.hoverHandlerId;
      renderEdgeHandler(context, shape, isActive);
    }
  }

  // Also render the original image content box (including overflow)
  renderContentOverlay(stageContext, node);
}

export function renderContentOverlay(stageContext, node) {
  const { context } = stageContext;
  const { scale } = stageContext.providers;

  // Content region in stage space (parallelogram if rotated)
  const content = computeClipBoxInStage(stageContext, node);
  const screenRegion = content.region.transform(scale.scale, scale.translateX, scale.translateY);

  // Draw the original image within the content bounds using affine transform
  if (node._img && node._img.img && content.__derived) {
    const { contentTL, contentTR, contentBL } = content.__derived;
    const s = scale.scale;
    const tx = scale.translateX;
    const ty = scale.translateY;

    // Compute stage-space vectors for content edges
    const uVec = { x: contentTR.x - contentTL.x, y: contentTR.y - contentTL.y };
    const vVec = { x: contentBL.x - contentTL.x, y: contentBL.y - contentTL.y };
    const imgW = node._img.img.width || 1;
    const imgH = node._img.img.height || 1;

    context.save();
    context.globalAlpha = 0.3;
    // Map image pixel space to screen parallelogram
    context.setTransform(
      s * (uVec.x / imgW),
      s * (uVec.y / imgW),
      s * (vVec.x / imgH),
      s * (vVec.y / imgH),
      contentTL.x * s + tx,
      contentTL.y * s + ty,
    );
    try {
      context.drawImage(node._img.img, 0, 0, imgW, imgH);
    } catch {
      // ignore drawing errors
    }
    context.restore();
  }

  // Green border for content region
  context.beginPath();
  context.setLineDash([6, 6]);
  context.moveTo(screenRegion.topLeft.x, screenRegion.topLeft.y);
  context.lineTo(screenRegion.topRight.x, screenRegion.topRight.y);
  context.lineTo(screenRegion.bottomRight.x, screenRegion.bottomRight.y);
  context.lineTo(screenRegion.bottomLeft.x, screenRegion.bottomLeft.y);
  context.closePath();
  context.strokeStyle = 'rgba(16,185,129,0.95)';
  context.lineWidth = 3;
  context.stroke();
  context.setLineDash([]);

  // Corner and edge handles (green)
  drawContentCornerHandle(context, screenRegion, 'topLeft');
  drawContentCornerHandle(context, screenRegion, 'topRight');
  drawContentCornerHandle(context, screenRegion, 'bottomLeft');
  drawContentCornerHandle(context, screenRegion, 'bottomRight');
  drawContentEdgeHandles(context, screenRegion);
}

export function drawContentCornerHandle(context, region, side) {
  const cornerSize = 22;
  const lineWidth = 6;
  const color = '#10B981';
  const shadow = 'rgba(16,185,129,0.25)';

  const getAngle = (p1, p2) => Math.atan2(p2.y - p1.y, p2.x - p1.x);

  // Corner point and edge-based angle
  let px = 0,
    py = 0,
    angle = 0;
  if (side === 'topLeft') {
    px = region.topLeft.x;
    py = region.topLeft.y;
    angle = getAngle(region.topLeft, region.topRight);
  } else if (side === 'topRight') {
    px = region.topRight.x;
    py = region.topRight.y;
    angle = getAngle(region.topLeft, region.topRight); // flip to keep +x inward
  } else if (side === 'bottomLeft') {
    px = region.bottomLeft.x;
    py = region.bottomLeft.y;
    angle = getAngle(region.bottomLeft, region.bottomRight);
  } else if (side === 'bottomRight') {
    px = region.bottomRight.x;
    py = region.bottomRight.y;
    angle = getAngle(region.bottomLeft, region.bottomRight); // flip to keep +x inward
  }

  context.save();
  context.shadowColor = shadow;
  context.shadowBlur = 6;
  context.lineWidth = lineWidth;
  context.strokeStyle = color;
  context.lineCap = 'round';
  context.translate(px, py);
  context.rotate(angle);
  context.beginPath();
  switch (side) {
    case 'topLeft':
      context.moveTo(0, cornerSize);
      context.lineTo(0, 0);
      context.lineTo(cornerSize, 0);
      break;
    case 'topRight':
      context.moveTo(0, cornerSize);
      context.lineTo(0, 0);
      context.lineTo(-cornerSize, 0);
      break;
    case 'bottomLeft':
      context.moveTo(0, -cornerSize);
      context.lineTo(0, 0);
      context.lineTo(cornerSize, 0);
      break;
    case 'bottomRight':
      context.moveTo(0, -cornerSize);
      context.lineTo(0, 0);
      context.lineTo(-cornerSize, 0);
      break;
  }
  context.stroke();
  context.restore();
}

export function drawContentEdgeHandles(context, region) {
  const handlerSize = 28;
  const thickness = 6;
  const color = '#10B981';
  const shadow = 'rgba(16,185,129,0.2)';
  context.save();
  context.fillStyle = color;
  context.shadowColor = shadow;
  context.shadowBlur = 6;

  const drawEdgeBar = (p1, p2, center, alongEdge) => {
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    const w = alongEdge ? handlerSize : thickness;
    const h = alongEdge ? thickness : handlerSize;
    context.save();
    context.translate(center.x, center.y);
    context.rotate(angle);
    context.beginPath();
    context.roundRect(-w / 2, -h / 2, w, h, 2);
    context.fill();
    context.restore();
  };

  const wLen = Math.hypot(
    region.topRight.x - region.topLeft.x,
    region.topRight.y - region.topLeft.y,
  );
  const hLen = Math.hypot(
    region.bottomLeft.x - region.topLeft.x,
    region.bottomLeft.y - region.topLeft.y,
  );

  if (wLen > 90) {
    const topMid = {
      x: (region.topLeft.x + region.topRight.x) / 2,
      y: (region.topLeft.y + region.topRight.y) / 2,
    };
    const bottomMid = {
      x: (region.bottomLeft.x + region.bottomRight.x) / 2,
      y: (region.bottomLeft.y + region.bottomRight.y) / 2,
    };
    // Along edge for top/bottom
    drawEdgeBar(region.topLeft, region.topRight, topMid, true);
    drawEdgeBar(region.bottomLeft, region.bottomRight, bottomMid, true);
  }
  if (hLen > 90) {
    const leftMid = {
      x: (region.topLeft.x + region.bottomLeft.x) / 2,
      y: (region.topLeft.y + region.bottomLeft.y) / 2,
    };
    const rightMid = {
      x: (region.topRight.x + region.bottomRight.x) / 2,
      y: (region.topRight.y + region.bottomRight.y) / 2,
    };
    // Perpendicular to edge for left/right
    drawEdgeBar(region.topLeft, region.bottomLeft, leftMid, true);
    drawEdgeBar(region.topRight, region.bottomRight, rightMid, true);
  }
  context.restore();
}

export function drawClipCornerHandle(context, x, y, side) {
  const cornerSize = 26;
  const lineWidth = 5;
  const color = '#007AFF';
  const shadow = 'rgba(0,122,255,0.25)';
  context.save();
  context.shadowColor = shadow;
  context.shadowBlur = 6;
  context.lineWidth = lineWidth;
  context.strokeStyle = color;
  context.lineCap = 'round';
  context.beginPath();
  switch (side) {
    case 'topLeft':
      context.moveTo(x, y + cornerSize);
      context.lineTo(x, y);
      context.lineTo(x + cornerSize, y);
      break;
    case 'topRight':
      context.moveTo(x - cornerSize, y);
      context.lineTo(x, y);
      context.lineTo(x, y + cornerSize);
      break;
    case 'bottomLeft':
      context.moveTo(x, y - cornerSize);
      context.lineTo(x, y);
      context.lineTo(x + cornerSize, y);
      break;
    case 'bottomRight':
      context.moveTo(x - cornerSize, y);
      context.lineTo(x, y);
      context.lineTo(x, y - cornerSize);
      break;
  }
  context.stroke();
  context.restore();
}

export function drawClipEdgeHandles(context, rect) {
  const handlerSize = 24;
  const thickness = 6;
  const color = '#007AFF';
  const shadow = 'rgba(0,122,255,0.2)';
  context.save();
  context.fillStyle = color;
  context.shadowColor = shadow;
  context.shadowBlur = 6;
  // top
  if (rect.w > 100) {
    context.beginPath();
    context.roundRect(
      rect.x + rect.w / 2 - handlerSize / 2,
      rect.y - thickness / 2,
      handlerSize,
      thickness,
      2,
    );
    context.fill();
    // bottom
    context.beginPath();
    context.roundRect(
      rect.x + rect.w / 2 - handlerSize / 2,
      rect.y + rect.h - thickness / 2,
      handlerSize,
      thickness,
      2,
    );
    context.fill();
  }
  if (rect.h > 100) {
    // left
    context.beginPath();
    context.roundRect(
      rect.x - thickness / 2,
      rect.y + rect.h / 2 - handlerSize / 2,
      thickness,
      handlerSize,
      2,
    );
    context.fill();
    // right
    context.beginPath();
    context.roundRect(
      rect.x + rect.w - thickness / 2,
      rect.y + rect.h / 2 - handlerSize / 2,
      thickness,
      handlerSize,
      2,
    );
    context.fill();
  }
  context.restore();
}

export function getContentSizeHandlers(stageContext, node) {
  if (!node) return [];
  // Compute content region in stage space
  const contentBox = computeClipBoxInStage(stageContext, node);
  const region = contentBox.region;

  // Helpers for hit-testing (scale-aware)
  const invScale = 1 / (stageContext.providers.scale.scale || 1);
  const cornerSize = 20 * invScale;
  const rectWidth = 22 * invScale;
  const rectHeight = 10 * invScale;

  // Corner handles (axis-aligned squares around corners)
  const corners = [
    { id: 'content-topLeft', pt: region.topLeft },
    { id: 'content-topRight', pt: region.topRight },
    { id: 'content-bottomLeft', pt: region.bottomLeft },
    { id: 'content-bottomRight', pt: region.bottomRight },
  ].map(({ id, pt }) => ({
    id,
    rect: new Rect(pt.x - 0.5 * cornerSize, pt.y - 0.5 * cornerSize, cornerSize, cornerSize),
  }));

  // Edge handles (small rotated rectangles centered on each edge)
  const edgeDefs = [
    { id: 'content-top', p1: region.topLeft, p2: region.topRight },
    { id: 'content-right', p1: region.topRight, p2: region.bottomRight, vertical: true },
    { id: 'content-bottom', p1: region.bottomLeft, p2: region.bottomRight },
    { id: 'content-left', p1: region.topLeft, p2: region.bottomLeft, vertical: true },
  ];

  const edges = edgeDefs.map((def) => {
    const mid = { x: (def.p1.x + def.p2.x) / 2, y: (def.p1.y + def.p2.y) / 2 };
    const w = def.vertical ? rectHeight : rectWidth;
    const h = def.vertical ? rectWidth : rectHeight;
    const baseRect = new Rect(mid.x - w / 2, mid.y - h / 2, w, h);
    const angle =
      (Math.atan2(def.p2.y - def.p1.y, def.p2.x - def.p1.x) * 180) / Math.PI +
      (def.vertical ? 90 : 0);
    const rotated = Region.fromRect(baseRect).rotate(angle);
    rotated.id = def.id;
    return { id: def.id, rect: rotated };
  });

  return [...corners, ...edges];
}

export function getContentHandlers(stageContext, node) {
  // Return the rotated content region for hit-testing
  const box = computeClipBoxInStage(stageContext, node);
  return {
    id: 'image-content',
    rect: box.region,
  };
}

export function clipEventActions(hitTestContext) {
  const { type, point, targetHandler, stageContext, provider } = hitTestContext;
  const interactive = provider;
  const clipMode = interactive.clipMode;
  if (!clipMode) return false;
  const node = stageContext.api.getNode(clipMode.nodeId);
  if (!node) return false;

  // Node region basis (handles rotation/transform)
  const region = stageContext.api.getNodeRegion(node);
  const TL = region.topLeft;
  const TR = region.topRight;
  const BL = region.bottomLeft;
  const u0 = { x: TR.x - TL.x, y: TR.y - TL.y }; // width vector
  const v0 = { x: BL.x - TL.x, y: BL.y - TL.y }; // height vector

  const clip = getNodeClip(node) || { x: 0, y: 0, w: 0, h: 0 };

  const setCursorByHandler = (id) => {
    const pure = id.replace(/^content-/, '').replace(/^viewport-/, '');
    provider.cursor.setById(pure);
  };

  // Local projection helper: solve delta = a*u0 + b*v0
  function projectDeltaToLocal(dx, dy) {
    const det = u0.x * v0.y - u0.y * v0.x || 1;
    const a = (dx * v0.y - dy * v0.x) / det;
    const b = (-dx * u0.y + dy * u0.x) / det;
    return { a, b };
  }

  if (type === EventTypes.down) {
    // If in clip mode and clicking other node, exit
    if (interactive.clipMode && type === EventTypes.down && targetHandler?.type !== 'imageClip') {
      interactive.exitClipMode();
    } else if (targetHandler && targetHandler.type === 'imageClip') {
      const id = targetHandler.id;
      clipMode.state.resizeEdge = id === 'image-content' ? 'move' : id;
      clipMode.state.startPoint = { ...point };
      clipMode.state.cache = { ...clip };
      // Cache starting content box to stabilize resize math across frames
      const startBox = computeClipBoxInStage(stageContext, node);
      // Use rotation-aware local sizes along node axes when available
      const cW0 = startBox.__derived?.contentLocalW || startBox.w;
      const cH0 = startBox.__derived?.contentLocalH || startBox.h;
      clipMode.state.dragBase = {
        contentW0: cW0,
        contentH0: cH0,
      };

      // Viewport-resize caching: preserve content size/offset in stage space
      if (id && id.startsWith('viewport-')) {
        const nodeRegion = stageContext.api.getNodeRegion(node);
        const TL = nodeRegion.topLeft;
        const TR = nodeRegion.topRight;
        const BL = nodeRegion.bottomLeft;
        const uVec = { x: TR.x - TL.x, y: TR.y - TL.y };
        const vVec = { x: BL.x - TL.x, y: BL.y - TL.y };
        const nodeW0 = Math.hypot(uVec.x, uVec.y) || 1;
        const nodeH0 = Math.hypot(vVec.x, vVec.y) || 1;
        const ux = { x: uVec.x / nodeW0, y: uVec.y / nodeW0 };
        const vx = { x: vVec.x / nodeH0, y: vVec.y / nodeH0 };

        const originalW = node._img?.img?.width || 1;
        const originalH = node._img?.img?.height || 1;
        const contentTL = startBox.__derived?.contentTL || { x: TL.x, y: TL.y };
        const sX0 = (startBox.__derived?.contentLocalW || 0) / (originalW || 1);
        const sY0 = (startBox.__derived?.contentLocalH || 0) / (originalH || 1);

        clipMode.state.viewport = {
          cacheNode: { x: node.x, y: node.y, w: node.w, h: node.h, id: node.id },
          basis: { ux, vx },
          nodeSize0: { w: nodeW0, h: nodeH0 },
          contentTL,
          sX0,
          sY0,
          originalW,
          originalH,
        };
      }
      provider.movingHandler = targetHandler;
      provider.hoverHandlerId = id;
      if (id === 'image-content') {
        provider.cursor.set('move');
      } else {
        setCursorByHandler(id);
      }
      return true;
    }
    return false;
  }

  if (type === EventTypes.move) {
    if (!clipMode.state.startPoint) {
      if (targetHandler && targetHandler.type === 'imageClip') {
        provider.hoverHandlerId = targetHandler.id;
        if (targetHandler.id === 'image-content') {
          provider.cursor.set('move');
        } else {
          setCursorByHandler(targetHandler.id);
        }
        return true;
      }
      provider.cursor.set('');
      return false;
    }

    const start = clipMode.state.startPoint;
    const cacheClip = clipMode.state.cache || { x: 0, y: 0, w: 0, h: 0 };
    const dx = point.x - start.x;
    const dy = point.y - start.y;
    const edge = clipMode.state.resizeEdge;

    if (edge === 'move') {
      const { a, b } = projectDeltaToLocal(dx, dy);
      // Scale deltas by current content scale to keep clip as ratios of the original image
      const scaleX = 1 + (cacheClip.w || 0);
      const scaleY = 1 + (cacheClip.h || 0);
      // Invert sign so dragging right/down moves content right/down
      const newClipInner = {
        x: cacheClip.x - a * scaleX,
        y: cacheClip.y - b * scaleY,
        w: cacheClip.w,
        h: cacheClip.h,
      };
      stageContext.api.updateNode(node.id, { clip: serializeClip(newClipInner) });
      provider.cursor.set('move');
      stageContext.hooks.onNodeMoveCallback();
      return true;
    }

    // Viewport resize handlers: resize node while preserving content size/offset in stage space
    if (edge && edge.startsWith('viewport-') && clipMode.state.viewport) {
      const vp = clipMode.state.viewport;
      const cacheNode = vp.cacheNode;

      // Current node basis (handles rotation)
      const region = stageContext.api.getNodeRegion(node);
      const TLn = region.topLeft;
      const TRn = region.topRight;
      const BLn = region.bottomLeft;
      const uVec = { x: TRn.x - TLn.x, y: TRn.y - TLn.y };
      const vVec = { x: BLn.x - TLn.x, y: BLn.y - TLn.y };
      const uLen = Math.hypot(uVec.x, uVec.y) || 1;
      const vLen = Math.hypot(vVec.x, vVec.y) || 1;
      const uUnit = { x: uVec.x / uLen, y: uVec.y / uLen };
      const vUnit = { x: vVec.x / vLen, y: vVec.y / vLen };

      // Project movement to node-local distances
      const { a, b } = projectDeltaToLocal(dx, dy);
      const dW = a * uLen;
      const dH = b * vLen;

      let newW = cacheNode.w;
      let newH = cacheNode.h;
      const kind = edge.replace('viewport-', '');

      if (kind === 'right') newW = cacheNode.w + dW;
      else if (kind === 'left') newW = cacheNode.w - dW;
      else if (kind === 'bottom') newH = cacheNode.h + dH;
      else if (kind === 'top') newH = cacheNode.h - dH;
      else if (kind === 'topLeft') {
        newW = cacheNode.w - dW;
        newH = cacheNode.h - dH;
      } else if (kind === 'topRight') {
        newW = cacheNode.w + dW;
        newH = cacheNode.h - dH;
      } else if (kind === 'bottomLeft') {
        newW = cacheNode.w - dW;
        newH = cacheNode.h + dH;
      } else if (kind === 'bottomRight') {
        newW = cacheNode.w + dW;
        newH = cacheNode.h + dH;
      }

      newW = Math.max(1, newW);
      newH = Math.max(1, newH);

      // Keep opposite handle (anchor) fixed in stage space
      const oppositeHandles = {
        topLeft: 'bottomRight',
        topRight: 'bottomLeft',
        bottomLeft: 'topRight',
        bottomRight: 'topLeft',
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left',
      };
      const anchorId = oppositeHandles[kind] || 'left';

      const getLocalHandleCoords = (id, w, h) => {
        switch (id) {
          case 'topLeft':
            return { x: -w / 2, y: -h / 2 };
          case 'topRight':
            return { x: w / 2, y: -h / 2 };
          case 'bottomLeft':
            return { x: -w / 2, y: h / 2 };
          case 'bottomRight':
            return { x: w / 2, y: h / 2 };
          case 'top':
            return { x: 0, y: -h / 2 };
          case 'bottom':
            return { x: 0, y: h / 2 };
          case 'left':
            return { x: -w / 2, y: 0 };
          case 'right':
            return { x: w / 2, y: 0 };
          default:
            return { x: -w / 2, y: 0 };
        }
      };

      // Compute cached center in STAGE space (respecting parent transform)
      let cx0 = cacheNode.x + cacheNode.w / 2;
      let cy0 = cacheNode.y + cacheNode.h / 2;
      const parentNodeForTransform = stageContext.api.getNode(node.parentId);
      if (parentNodeForTransform) {
        const pReg = stageContext.api.getNodeRegion(parentNodeForTransform);
        const pTL = pReg.topLeft;
        const pTR = pReg.topRight;
        const pBL = pReg.bottomLeft;
        const puVec = { x: pTR.x - pTL.x, y: pTR.y - pTL.y };
        const pvVec = { x: pBL.x - pTL.x, y: pBL.y - pTL.y };
        const puLen = Math.hypot(puVec.x, puVec.y) || 1;
        const pvLen = Math.hypot(pvVec.x, pvVec.y) || 1;
        const puUnit = { x: puVec.x / puLen, y: puVec.y / puLen };
        const pvUnit = { x: pvVec.x / pvLen, y: pvVec.y / pvLen };
        // Map parent-local center (cx0, cy0) to stage using parent's basis
        const stageCx = pTL.x + puUnit.x * cx0 + pvUnit.x * cy0;
        const stageCy = pTL.y + puUnit.y * cx0 + pvUnit.y * cy0;
        cx0 = stageCx;
        cy0 = stageCy;
      }
      const angle = Math.atan2(uUnit.y, uUnit.x);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      const anchorLocal0 = getLocalHandleCoords(anchorId, cacheNode.w, cacheNode.h);
      const anchorGlobalX = cx0 + anchorLocal0.x * cos - anchorLocal0.y * sin;
      const anchorGlobalY = cy0 + anchorLocal0.x * sin + anchorLocal0.y * cos;

      const anchorLocal1 = getLocalHandleCoords(anchorId, newW, newH);
      const newCx = anchorGlobalX - (anchorLocal1.x * cos - anchorLocal1.y * sin);
      const newCy = anchorGlobalY - (anchorLocal1.x * sin + anchorLocal1.y * cos);

      // Convert new center (stage) back to parent-local x/y for node placement
      const patchNode = { x: 0, y: 0, w: newW, h: newH };
      const parentForPlacement = stageContext.api.getNode(node.parentId);
      if (parentForPlacement) {
        const pReg = stageContext.api.getNodeRegion(parentForPlacement);
        const pTL = pReg.topLeft;
        const pTR = pReg.topRight;
        const pBL = pReg.bottomLeft;
        const puVec = { x: pTR.x - pTL.x, y: pTR.y - pTL.y };
        const pvVec = { x: pBL.x - pTL.x, y: pBL.y - pTL.y };
        const puLen = Math.hypot(puVec.x, puVec.y) || 1;
        const pvLen = Math.hypot(pvVec.x, pvVec.y) || 1;
        const puUnit = { x: puVec.x / puLen, y: puVec.y / puLen };
        const pvUnit = { x: pvVec.x / pvLen, y: pvVec.y / pvLen };
        const dx = newCx - pTL.x;
        const dy = newCy - pTL.y;
        const aParent = dx * puUnit.x + dy * puUnit.y;
        const bParent = dx * pvUnit.x + dy * pvUnit.y;
        patchNode.x = aParent - newW / 2;
        patchNode.y = bParent - newH / 2;
      } else {
        // No parent: stage == local
        patchNode.x = newCx - newW / 2;
        patchNode.y = newCy - newH / 2;
      }

      // Preserve content size and offset: keep contentLocalW/H and contentTL
      const cW0 = clipMode.state.dragBase?.contentW0 || 1;
      const cH0 = clipMode.state.dragBase?.contentH0 || 1;
      const nextClip = {
        w: Math.max(-0.99, newW / cW0 - 1),
        h: Math.max(-0.99, newH / cH0 - 1),
        x: 0,
        y: 0,
      };

      // Maintain contentTL invariant (compute purely in stage space)
      const tl1x = newCx + (-newW / 2) * uUnit.x + (-newH / 2) * vUnit.x;
      const tl1y = newCy + (-newW / 2) * uUnit.y + (-newH / 2) * vUnit.y;
      const deltaTLx = tl1x - vp.contentTL.x;
      const deltaTLy = tl1y - vp.contentTL.y;
      const offsetLocalX1 = deltaTLx * uUnit.x + deltaTLy * uUnit.y;
      const offsetLocalY1 = deltaTLx * vUnit.x + deltaTLy * vUnit.y;
      nextClip.x = offsetLocalX1 / (cW0 || 1);
      nextClip.y = offsetLocalY1 / (cH0 || 1);

      stageContext.api.updateNode(node.id, { ...patchNode, clip: serializeClip(nextClip) });
      provider.hoverHandlerId = edge;
      stageContext.hooks.onNodeResizeCallback();
      return true;
    }

    if (edge && edge.startsWith('content-')) {
      const { a, b } = projectDeltaToLocal(dx, dy);
      const uLen = Math.hypot(u0.x, u0.y) || 1;
      const vLen = Math.hypot(v0.x, v0.y) || 1;
      const dragBase = clipMode.state.dragBase || { contentW0: 1, contentH0: 1 };
      const baseW = (dragBase.contentW0 || 1) * (1 + (cacheClip.w || 0));
      const baseH = (dragBase.contentH0 || 1) * (1 + (cacheClip.h || 0));

      const minContentSize = 1; // stage units

      function clampW(w) {
        return Math.max(-0.99, w);
      }
      function clampH(h) {
        return Math.max(-0.99, h);
      }

      const next = { ...cacheClip };
      const kind = edge.replace('content-', '');

      if (kind === 'right') {
        const dr = a * uLen;
        const cW1 = Math.max(minContentSize, dragBase.contentW0 + dr);
        const w1 = clampW(baseW / cW1 - 1);
        const ratioX = (1 + w1) / (1 + cacheClip.w || 1);
        next.w = w1;
        next.x = cacheClip.x * ratioX;
      } else if (kind === 'left') {
        const dL = a * uLen;
        const cW1 = Math.max(minContentSize, dragBase.contentW0 - dL);
        const w1 = clampW(baseW / cW1 - 1);
        const deltaX = -(w1 - cacheClip.w) / (1 + cacheClip.w || 1);
        next.w = w1;
        next.x = cacheClip.x + deltaX;
      } else if (kind === 'bottom') {
        const db = b * vLen;
        const cH1 = Math.max(minContentSize, dragBase.contentH0 + db);
        const h1 = clampH(baseH / cH1 - 1);
        const ratioY = (1 + h1) / (1 + cacheClip.h || 1);
        next.h = h1;
        next.y = cacheClip.y * ratioY;
      } else if (kind === 'top') {
        const dT = b * vLen;
        const cH1 = Math.max(minContentSize, dragBase.contentH0 - dT);
        const h1 = clampH(baseH / cH1 - 1);
        const deltaY = -(h1 - cacheClip.h) / (1 + cacheClip.h || 1);
        next.h = h1;
        next.y = cacheClip.y + deltaY;
      } else if (kind === 'topRight') {
        const dr = a * uLen;
        const cW1 = Math.max(minContentSize, dragBase.contentW0 + dr);
        const w1 = clampW(baseW / cW1 - 1);
        const ratioX = (1 + w1) / (1 + cacheClip.w || 1);
        next.w = w1;
        next.x = cacheClip.x * ratioX;
        const dT = b * vLen;
        const cH1 = Math.max(minContentSize, dragBase.contentH0 - dT);
        const h1 = clampH(baseH / cH1 - 1);
        const deltaY = -(h1 - cacheClip.h) / (1 + cacheClip.h || 1);
        next.h = h1;
        next.y = cacheClip.y + deltaY;
      } else if (kind === 'bottomRight') {
        const dr = a * uLen;
        const cW1 = Math.max(minContentSize, dragBase.contentW0 + dr);
        const w1 = clampW(baseW / cW1 - 1);
        const ratioX = (1 + w1) / (1 + cacheClip.w || 1);
        next.w = w1;
        next.x = cacheClip.x * ratioX;
        const db = b * vLen;
        const cH1 = Math.max(minContentSize, dragBase.contentH0 + db);
        const h1 = clampH(baseH / cH1 - 1);
        const ratioY = (1 + h1) / (1 + cacheClip.h || 1);
        next.h = h1;
        next.y = cacheClip.y * ratioY;
      } else if (kind === 'bottomLeft') {
        const dL = a * uLen;
        const cW1 = Math.max(minContentSize, dragBase.contentW0 - dL);
        const w1 = clampW(baseW / cW1 - 1);
        const deltaX = -(w1 - cacheClip.w) / (1 + cacheClip.w || 1);
        next.w = w1;
        next.x = cacheClip.x + deltaX;
        const db = b * vLen;
        const cH1 = Math.max(minContentSize, dragBase.contentH0 + db);
        const h1 = clampH(baseH / cH1 - 1);
        const ratioY = (1 + h1) / (1 + cacheClip.h || 1);
        next.h = h1;
        next.y = cacheClip.y * ratioY;
      } else if (kind === 'topLeft') {
        const dL = a * uLen;
        const cW1 = Math.max(minContentSize, dragBase.contentW0 - dL);
        const w1 = clampW(baseW / cW1 - 1);
        const deltaX = -(w1 - cacheClip.w) / (1 + cacheClip.w || 1);
        next.w = w1;
        next.x = cacheClip.x + deltaX;
        const dT = b * vLen;
        const cH1 = Math.max(minContentSize, dragBase.contentH0 - dT);
        const h1 = clampH(baseH / cH1 - 1);
        const deltaY = -(h1 - cacheClip.h) / (1 + cacheClip.h || 1);
        next.h = h1;
        next.y = cacheClip.y + deltaY;
      }

      next.w = Math.max(-0.99, next.w);
      next.h = Math.max(-0.99, next.h);
      stageContext.api.updateNode(node.id, { clip: serializeClip(next) });
      provider.hoverHandlerId = edge;
      stageContext.hooks.onNodeResizeCallback();
      return true;
    }

    return false;
  }

  if (type === EventTypes.up) {
    clipMode.state.startPoint = null;
    clipMode.state.cache = null;
    clipMode.state.resizeEdge = null;
    provider.cursor.set('');
    provider.movingHandler = null;
    provider.hoverHandlerId = '';
    return true;
  }
  return false;
}

// Return interactive handler rects for image clip overlay, to register in getInteractiveHandlers later
export function getInteractiveImageClipHandlers(stageContext, node) {
  if (!stageContext || !node) return [];
  const nodeRegion = stageContext.api.getNodeRegion(node);
  const handlers = getContentSizeHandlers(stageContext, node);
  const contentHandler = getContentHandlers(stageContext, node);
  const viewportHandlers = getViewportHandlers(stageContext, nodeRegion);
  return [...handlers, ...viewportHandlers, contentHandler].map((h) => {
    const shape = h.rect;
    // Support both Rect and Region shapes
    if (shape && typeof shape.x === 'number' && typeof shape.w === 'number') {
      const r = new Rect(shape.x, shape.y, shape.w, shape.h);
      r.id = h.id;
      r.type = 'imageClip';
      return r;
    } else {
      // Assume Region-like
      const r = shape;
      r.id = h.id;
      r.type = 'imageClip';
      return r;
    }
  });
}

// Viewport handlers (resize the node bounds)
export function getViewportHandlers(stageContext, nodeRegion) {
  // Scale-aware sizes
  const invScale = 1;
  const cornerSize = 24 * invScale;
  const rectWidth = 32 * invScale;
  const rectHeight = 14 * invScale;

  // Corner handlers around node corners
  const corners = [
    { id: 'viewport-topLeft', pt: nodeRegion.topLeft },
    { id: 'viewport-topRight', pt: nodeRegion.topRight },
    { id: 'viewport-bottomLeft', pt: nodeRegion.bottomLeft },
    { id: 'viewport-bottomRight', pt: nodeRegion.bottomRight },
  ].map(({ id, pt }) => {
    const rect = new Rect(pt.x - 0.5 * cornerSize, pt.y - 0.5 * cornerSize, cornerSize, cornerSize);
    rect.id = id;
    return { id: id, rect: rect };
  });

  // Edge handlers at midpoints (rotated small rects)
  const makeRotatedEdge = (id, p1, p2, vertical = false) => {
    const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    const angle = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI + (vertical ? 90 : 0);
    const w = vertical ? rectHeight : rectWidth;
    const h = vertical ? rectWidth : rectHeight;
    const baseRect = new Rect(mid.x - w / 2, mid.y - h / 2, w, h);
    const rotated = Region.fromRect(baseRect).rotate(angle);
    rotated.id = id;
    return { id: id, rect: rotated };
  };

  const edges = [
    makeRotatedEdge('viewport-top', nodeRegion.topLeft, nodeRegion.topRight, false),
    makeRotatedEdge('viewport-right', nodeRegion.topRight, nodeRegion.bottomRight, true),
    makeRotatedEdge('viewport-bottom', nodeRegion.bottomLeft, nodeRegion.bottomRight, false),
    makeRotatedEdge('viewport-left', nodeRegion.topLeft, nodeRegion.bottomLeft, true),
  ];

  return [...corners, ...edges];
}
